import { type ComponentProps, type ReactNode, useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const alertVariants = cva(
  'tw-grid tw-grid-cols-[1fr_auto] tw-items-center tw-gap-6 tw-w-full tw-rounded-[8px] tw-border tw-border-solid tw-shadow-[0_0.0625rem_0.125rem_rgba(0,0,0,0.15),0_0.0625rem_0.25rem_rgba(0,0,0,0.15)]',
  {
    variants: {
      variant: {
        default: 'tw-bg-background tw-text-foreground',
        danger: 'tw-bg-[#F6EDF0] tw-border-[#F6EDF0] tw-text-[#000000]',
        success: 'tw-bg-[#F2FAF7] tw-border-[#F2FAF7] tw-text-[#000000]',
        info: 'tw-bg-[#EFF8FA] tw-border-[#EFF8FA] tw-text-[#000000]',
        warning: 'tw-bg-[#FFFADB] tw-border-[#FFFADB] tw-text-[#000000]',
      },
      size: {
        default: 'tw-px-6 tw-py-6 tw-text-sm',
        compact: 'tw-px-4 tw-py-4 tw-text-xs tw-mt-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const AlertAction = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    data-slot="alert-action"
    className={cn('tw-flex tw-items-start tw-flex-shrink-0', className)}
    {...props}
  />
);

const AlertTitle = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    data-slot="alert-title"
    className={cn(
      'tw-mb-1 tw-text-base tw-font-bold tw-leading-none tw-tracking-tight',
      className,
    )}
    {...props}
  />
);

const AlertDescription = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    data-slot="alert-description"
    className={cn(
      'tw-text-sm group-has-[>svg]/alert:tw-col-start-2',
      className,
    )}
    {...props}
  />
);

const Alert = ({
  className,
  variant,
  size,
  children,
  dismissible,
  dismissLabel,
  onClose,
  ...props
}: ComponentProps<'div'> &
  VariantProps<typeof alertVariants> & {
    children: ReactNode;
    dismissible?: boolean;
    dismissLabel?: string;
    onClose?: () => void;
  }) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    if (onClose !== undefined) {
      onClose();
    } else {
      setIsVisible(false);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant, size }), className)}
      {...props}
    >
      <div className="tw-grid tw-gap-1 has-[>svg]:tw-grid-cols-[auto_1fr] has-[>svg]:tw-gap-x-2 *:[svg]:tw-row-span-2 tw-group/alert">
        {children}
      </div>

      {dismissible && (
        <AlertAction>
          <button
            type="button"
            onClick={handleClose}
            className="tw-text-[#00688d] tw-py-[0.4375rem] tw-px-3 tw-underline tw-bg-transparent tw-border-none tw-p-0 tw-cursor-pointer hover:tw-text-[rgba(0,104,141,1)] hover:tw-underline focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2"
          >
            {dismissLabel ?? 'Dismiss'}
          </button>
        </AlertAction>
      )}
    </div>
  );
};

export { Alert, AlertTitle, AlertDescription, AlertAction };
